import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSapiensAuth, useRequireRole } from '@sapiens/auth-react';
import { LoadingSpinner } from './LoadingSpinner';

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Require user to be authenticated
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const { isAuthenticated, isLoading } = useSapiensAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

interface RequireRoleProps {
  children: ReactNode;
  requiredUserType: number;
  fallbackPath?: string;
}

/**
 * Require user to have a minimum role level
 */
export function RequireRole({ 
  children, 
  requiredUserType,
  fallbackPath = '/unauthorized'
}: RequireRoleProps) {
  const { hasRole, isLoading } = useRequireRole(requiredUserType);
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!hasRole) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * Require user to be admin (userType === 2)
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  return <RequireRole requiredUserType={2}>{children}</RequireRole>;
}
